// Funções da UI sem DOM (testadas em node: web/test/ui-lib.test.ts).
import { effect, type ReadonlySignal } from "@preact/signals";
import type { Card, CardKind, IngestState, Tag } from "../../../shared/types.ts";
import type { SyncStatus } from "../data/types.ts";
import type { SearchClient } from "../search/types.ts";

export const KIND_LABEL: Record<CardKind, string> = {
  note: "nota", image: "imagem", link: "link", quote: "citação", highlight: "destaque",
  color: "cor", pdf: "pdf", video: "vídeo", audio: "áudio", file: "arquivo",
};

/** Formatos com texto próprio: o detalhe mostra o editor (a cor também: os códigos ficam editáveis). */
export const TEXT_KINDS: ReadonlySet<CardKind> = new Set(["note", "quote", "highlight", "color"]);
/**
 * Formatos do menu Tipo: só os que mostram o mesmo conteúdo (D11). Texto troca entre tipos de texto. Nota → Imagem
 * escondia o texto; Imagem → Vídeo, a foto.
 * ponytail: fora do texto só o próprio tipo (o arquivo do card pode não ser do tipo novo); um endereço sem arquivo
 * (link do YouTube, de um podcast) troca entre link, vídeo e áudio. Trocar entre os de arquivo quando o tipo do arquivo vier no card.
 */
const ADDRESS_KINDS: ReadonlySet<CardKind> = new Set(["link", "video", "audio"]);
export function kindChoices(c: Pick<Card, "kind" | "primaryBlobId" | "sourceUrl">): CardKind[] {
  if (TEXT_KINDS.has(c.kind)) return (Object.keys(KIND_LABEL) as CardKind[]).filter((k) => TEXT_KINDS.has(k));
  // Endereço sem arquivo troca entre link, vídeo e áudio: shownAs (detail/Media.tsx) mostra o link e o player.
  return ADDRESS_KINDS.has(c.kind) && !c.primaryBlobId && c.sourceUrl ? [...ADDRESS_KINDS] : [c.kind];
}

/** Assuntos Schema.org que a ingestão grava (JSON-LD da página ou a IA), em português. Um tipo fora da lista fica como veio. */
export const ENTITY_LABEL: Record<string, string> = {
  Article: "Artigo", NewsArticle: "Notícia", BlogPosting: "Post de blog", VideoObject: "Vídeo", Movie: "Filme", Book: "Livro",
  Product: "Produto", Recipe: "Receita", Repository: "Repositório", SocialMediaPosting: "Post", Photograph: "Foto", Place: "Lugar",
  Event: "Evento", MusicRecording: "Música", MusicAlbum: "Álbum", Course: "Curso", Person: "Pessoa", Organization: "Organização",
  CreativeWork: "Obra", WebPage: "Página", WebSite: "Site", ImageObject: "Imagem", Painting: "Pintura", VisualArtwork: "Arte",
  Podcast: "Podcast", PodcastSeries: "Podcast", PodcastEpisode: "Episódio de podcast", TVSeries: "Série", TVEpisode: "Episódio",
  VideoGame: "Jogo", SoftwareApplication: "App", MobileApplication: "App", Restaurant: "Restaurante", LocalBusiness: "Lugar",
  Hotel: "Hospedagem", Review: "Resenha", Quotation: "Citação", Map: "Mapa", Dataset: "Dados",
};
export const entityLabel = (t: string) => ENTITY_LABEL[t] ?? t;

export const INGEST_LABEL: Partial<Record<IngestState, string>> = {
  uploading: "Enviando", queued: "Processando", processing: "Processando", partial: "Parcial", failed: "Falhou",
};

/** "Parcial" que a pessoa resolve (a página pede login ou recusou o acesso): o resto só se explica no detalhe. */
const ACTIONABLE_PARTIAL: ReadonlySet<string> = new Set(["login_required", "fetch_401", "fetch_403"]);
/**
 * Aviso de ingestão na grade e nas coleções. Parcial por IA desligada, cota, formato que a IA não lê, captura ou busca
 * por significado não muda o card: na grade fica sem aviso (o detalhe explica). Enviando, Processando e Falhou ficam.
 */
export function gridNotice(c: Pick<Card, "ingestState" | "ingestError">): IngestState | null {
  if (!INGEST_LABEL[c.ingestState]) return null;
  if (c.ingestState === "partial" && !ACTIONABLE_PARTIAL.has(c.ingestError ?? "")) return null;
  return c.ingestState;
}

// Códigos que o consumidor de ingestão grava em ingest_error (src/ingest/consumer.ts).
const INGEST_ERROR: Record<string, string> = {
  fetch_401: "a página pede login",
  fetch_403: "a página recusou o acesso",
  fetch_404: "a página não existe mais",
  fetch_410: "a página não existe mais",
  fetch_429: "a página pediu para esperar; tente mais tarde",
  fetch_timeout: "a página demorou demais",
  too_large: "arquivo grande demais",
  upload_refused: "o servidor não aceitou o envio; o arquivo continua neste aparelho", // store.upload (4xx)
  unsupported: "formato não suportado",
  unsupported_type: "não é uma página que dê para ler",
  blocked_host: "endereço não permitido",
  redirect_loop: "a página redireciona sem parar",
  network: "não deu para alcançar o site",
  network_dns: "esse endereço não existe (o domínio não foi encontrado)",
  // Parcial (fase 3): o card fica, só falta uma parte da análise.
  login_required: "a página pede login; ficou só o link",
  screenshot_failed: "não deu para fotografar a página",
  ai_unavailable: "a análise automática está desligada",
  ai_refused: "a IA preferiu não analisar este conteúdo",
  ai_invalid: "a IA respondeu algo que não deu para usar",
  ai_error: "a IA falhou desta vez",
  ai_quota: "a cota de análises de hoje acabou; continua amanhã",
  image_unsupported: "a IA não lê este formato de imagem",
  image_too_large: "a imagem é grande demais para a análise automática",
  pdf_failed: "não deu para ler o PDF",
  pdf_no_text: "o PDF não tem texto selecionável",
  embed_failed: "ficou fora da busca por significado",
};
/**
 * Sem a chave da Anthropic a análise termina em partial/ai_unavailable: o card está completo para o que existe hoje
 * (título, capa, leitura, busca), então nada fica com cara de erro. O detalhe diz, calmo, que o resumo vem depois.
 */
export const quietIngest = (c: Pick<Card, "ingestState" | "ingestError">) => c.ingestState === "partial" && c.ingestError === "ai_unavailable";

export const ingestReason = (code: string | null): string => {
  if (!code) return "";
  if (INGEST_ERROR[code]) return INGEST_ERROR[code];
  if (/^fetch_4\d\d$/.test(code)) return "a página recusou o pedido";
  if (/^fetch_5\d\d$/.test(code)) return "o site está com problema; tente mais tarde";
  return "não deu para processar";
};

const rtf = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 31_536_000], ["month", 2_592_000], ["day", 86_400], ["hour", 3_600], ["minute", 60],
];
/** "agora", "há 5 minutos", "ontem", "há 3 meses". */
export function relativeTime(ts: number, now = Date.now()): string {
  const s = (ts - now) / 1000;
  if (Math.abs(s) < 45) return "agora";
  const [unit, size] = UNITS.find(([, n]) => Math.abs(s) >= n) ?? ["minute", 60];
  return rtf.format(Math.round(s / size), unit);
}
export const absoluteTime = (ts: number) => new Date(ts).toLocaleString("pt-BR", { dateStyle: "long", timeStyle: "short" });

/** Campo onde a pessoa está digitando: atalhos de uma tecla não podem agir aqui. */
export function isTypingTarget(t: unknown): boolean {
  const el = t as { tagName?: string; type?: string; isContentEditable?: boolean } | null;
  if (!el) return false;
  if (el.isContentEditable || el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  return el.tagName === "INPUT" && !["button", "checkbox", "radio", "submit", "reset", "file", "range", "color"].includes(el.type ?? "text");
}

export type Shortcut = "note" | "search" | "help" | "prev" | "next";
type KeyLike = { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean; isComposing?: boolean; target: unknown };
/** Atalhos de uma tecla. Nunca com Ctrl/Cmd/Alt (são do navegador) nem enquanto se digita. */
export function shortcutFor(e: KeyLike): Shortcut | null {
  if (e.ctrlKey || e.metaKey || e.altKey || e.isComposing || isTypingTarget(e.target)) return null;
  const map: Record<string, Shortcut> = { n: "note", N: "note", "/": "search", "?": "help", ArrowLeft: "prev", ArrowRight: "next" };
  return map[e.key] ?? null;
}

/** Texto colado que é só uma URL http(s). */
export function urlFromText(text: string): string | null {
  const s = text.trim();
  if (!/^https?:\/\/\S+$/i.test(s)) return null;
  try {
    return new URL(s).href;
  } catch {
    return null;
  }
}

/**
 * O que se digita no campo Link: URL http(s), ou endereço sem esquema ("exemplo.com/x", como o iPhone cola de outros
 * apps), que vira https://. Outro esquema (mailto:, javascript:) ou texto sem domínio com ponto: null.
 */
export function linkFromInput(text: string): string | null {
  const s = text.trim();
  const url = urlFromText(s);
  if (url || !s || /\s/.test(s) || /^[a-z][\w+.-]*:(?!\d)/i.test(s)) return url;
  try {
    const u = new URL(`https://${s}`);
    return u.hostname.includes(".") ? u.href : null;
  } catch {
    return null;
  }
}

/** Só #rrggbb vira estilo: cor vinda dos dados nunca injeta url() nem outra coisa. */
export const safeColor = (c: string | null | undefined) => (c && /^#[0-9a-f]{6}$/i.test(c) ? c : undefined);

export const cardText = (c: Card) => c.title || c.contentText || c.noteText || c.sourceDomain || KIND_LABEL[c.kind];

/** Imagem do card: a foto ou a capa do link (/media, §7.9); enquanto a foto sobe, a prévia local (object URL). */
export function cardImage(c: Card, previews: ReadonlyMap<string, string>, w: number): string | null {
  const blob = c.kind === "image" ? c.primaryBlobId : c.kind === "link" ? c.coverBlobId : null;
  if (blob) return `/media/${encodeURIComponent(blob)}?w=${w}`;
  return c.kind === "image" ? (previews.get(c.id) ?? null) : null;
}

// ponytail: inicial do domínio no lugar do favicon (o card não traz favicon); trocar quando a ingestão gravar um.
export const domainInitial = (domain: string | null) => (domain ?? "").replace(/^www\./, "").charAt(0).toUpperCase();

export type SyncState = "ok" | "busy" | "offline" | "error";
/** Estado do sync em texto. Cada estado tem ícone de forma própria: na tela estreita o texto some e a cor não basta. */
export function syncView(s: SyncStatus): [SyncState, string] {
  if (!s.online) return ["offline", s.pending ? `Offline · ${s.pending} na fila` : "Offline"];
  if (s.error === "unauthorized") return ["error", "Sessão expirada: entre de novo"];
  if (s.error) return ["error", "Erro ao sincronizar"];
  if (s.pending || s.syncing) return ["busy", s.pending ? `Sincronizando · ${s.pending}` : "Sincronizando"];
  return ["ok", "Sincronizado"];
}

/**
 * Índice de busca acompanha o store: load na primeira vez, depois update só com o que mudou (identidade do objeto:
 * o store troca só os cards alterados). `done` roda depois de cada mudança enviada ao índice. Devolve o dispose.
 */
export function syncIndex(
  cards: ReadonlySignal<Card[]>,
  tags: ReadonlySignal<Map<string, Tag>>,
  client: Pick<SearchClient, "load" | "update">,
  done: () => void,
  failed: () => void,
): () => void {
  let indexed: Map<string, Card> | null = null;
  let indexedTags: Map<string, Tag> | null = null;
  let chain = Promise.resolve();
  return effect(() => {
    const list = cards.value;
    const t = tags.value;
    const before = indexed;
    const tagsChanged = t !== indexedTags;
    const now = (indexed = new Map(list.map((c) => [c.id, c])));
    indexedTags = t;
    chain = chain
      .then(async () => {
        if (!before) await client.load(list, t);
        else {
          const changed = list.filter((c) => before.get(c.id) !== c);
          const removed = [...before.keys()].filter((id) => !now.has(id));
          if (!changed.length && !removed.length && !tagsChanged) return;
          await client.update(changed, removed, t);
        }
        done();
      })
      .catch(failed);
  });
}

/** Nota longa no grid (DESIGN-MATCH §3.5): mais de 140 caracteres ou mais de 3 blocos vira 12/18 com recorte. */
export const isLongNote = (c: Card) => (c.contentText ?? "").length > 140 || (c.content?.content?.length ?? 0) > 3;

/** Altura estimada do card em larguras de coluna (só para distribuir nas colunas). Legenda ≈ 0,1. */
/** Altura ÷ largura da face de PDF, vídeo, áudio e arquivo na grade (web/src/ui/detail/Media.tsx). */
export const FILE_FACE: Partial<Record<CardKind, number>> = { pdf: 1.3, video: 0.5625, audio: 0.75, file: 0.75 };

export function estimateHeight(c: Card): number {
  const caption = (c.kind === "link" ? c.sourceDomain : c.title) ? 0.1 : 0;
  if (FILE_FACE[c.kind]) return caption + FILE_FACE[c.kind]!;
  if (c.kind === "image") return caption + (c.width && c.height ? Math.min(3, Math.max(0.3, c.height / c.width)) : 1);
  if (c.kind === "link") return caption + 1;
  if (c.kind === "color") return caption + 0.5; // face de amostras 2:1
  const len = cardText(c).length;
  return caption + (isLongNote(c) ? Math.min(1.05, 0.14 + Math.ceil(len / 45) * 0.06) : 0.14 + Math.ceil(len / 22) * 0.09);
}

export type Density = "amplo" | "medio" | "compacto";
/** Densidade da grade: largura mínima da coluna (ref: três níveis de grade). */
export const DENSITY: Record<Density, { label: string; min: number }> = {
  amplo: { label: "Amplo", min: 285 },
  medio: { label: "Médio", min: 214 },
  compacto: { label: "Compacto", min: 143 },
};

/**
 * Colunas da grade (DESIGN-MATCH §2): cols = clamp(2, ⌊(W + gap) / (min + gap)⌋, 8), W = viewport − barra de rolagem
 * − 2 × gutter. Faixas iguais às media queries do app.css: > 1024 gutter 80 / gap 25; 769–1024 20 / 20; celular sempre 2.
 */
export function columnsFor(viewport: number, scrollbar = 0, min = DENSITY.amplo.min): number {
  if (viewport <= 768) return 2;
  const [gutter, gap] = viewport > 1024 ? [80, 25] : [20, 20];
  return Math.max(2, Math.min(8, Math.floor((viewport - scrollbar - 2 * gutter + gap) / (min + gap))));
}

/** Masonry guloso: cada item vai para a coluna mais baixa. Prefixo estável: acrescentar itens não move os anteriores. */
export function masonry<T>(items: T[], cols: number, height: (t: T) => number): T[][] {
  const out: T[][] = Array.from({ length: cols }, () => []);
  const hs = out.map(() => 0);
  for (const it of items) {
    const i = hs.indexOf(Math.min(...hs));
    out[i].push(it);
    hs[i] += height(it) + 0.06;
  }
  return out;
}

/** Contraste WCAG entre duas cores #rrggbb. */
export function contrast(a: string, b: string): number {
  const lum = (h: string) => {
    const [r, g, bl] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return 0.2126 * r + 0.7152 * g + 0.0722 * bl;
  };
  const [hi, lo] = [lum(a), lum(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Roda `done` depois de `ms`, adiando de `again` em `again` enquanto `held()` (aviso com foco ou mouse em cima: WCAG 2.2.1). */
export function expireUnlessHeld(held: () => boolean, done: () => void, ms: number, again = 1000) {
  const tick = () => void (held() ? setTimeout(tick, again) : done());
  setTimeout(tick, ms);
}

/** Aviso antes de sair: o logout limpa o armazenamento do site (Clear-Site-Data) e a fila de saída vai junto. */
export function unsentWarning(pending: number, uploading: number): string | null {
  // ponytail: o maior dos dois, não a soma (um envio em curso costuma ser também uma op da fila)
  const n = Math.max(pending, uploading);
  if (!n) return null;
  const [what, them] = n === 1 ? ["1 item ainda não foi enviado", "ele será apagado"] : [`${n} itens ainda não foram enviados`, "eles serão apagados"];
  return `${what} ao servidor. Se sair agora, ${them} deste aparelho. Sair mesmo assim?`;
}
